/** Strips the Dataverse `KA-` prefix ("KA-02696" -> "02696") and trims whitespace. */
export const normalizeArticleNumber = (value?: string | null): string => {
  if (!value) return "";

  return value.trim().replace(/^KA-/i, "");
};

/**
 * Extracts an article number from an old-portal KB article url
 * (e.g. ".../kb/KA-02696" or ".../kb/02696"). Returns null when the url
 * doesn't point at a KB article.
 */
export const getArticleNumberFromUrl = (url?: string | null): string | null => {
  if (!url) return null;

  const match = url.match(/\/kb\/(?:KA-)?(\d+)/i);
  return match ? match[1] : null;
};

/** Builds the internal path to the KB article page. */
export const buildArticlePath = (
  lang?: string | null,
  number?: string | null,
): string => {
  return `/${lang}/support/kb/${normalizeArticleNumber(number)}`;
};

/** Turns heading text into a URL-safe anchor id, matching the old portal's TOC behavior. */
export const slugify = (text: string): string => {
  return text
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
};

export type MarkdownHeadingType = {
  level: number;
  text: string;
  id: string;
};

/**
 * Parses ATX-style markdown headings ("# Title") into a flat, ordered list for
 * building an "In this article" table of contents. Duplicate ids get a
 * "-2", "-3", ... suffix, matching the order they appear in the document.
 */
export const extractMarkdownHeadings = (text?: string | null): MarkdownHeadingType[] => {
  if (!text) return [];

  const usedIds = new Map<string, number>();
  const headings: MarkdownHeadingType[] = [];

  for (const line of text.split("\n")) {
    const match = line.match(/^(#{1,6})\s+(.+)$/);
    if (!match) continue;

    const level = match[1].length;
    const headingText = match[2].trim();
    const baseId = slugify(headingText);

    const count = usedIds.get(baseId) ?? 0;
    usedIds.set(baseId, count + 1);
    const id = count === 0 ? baseId : `${baseId}-${count + 1}`;

    headings.push({ level, text: headingText, id });
  }

  return headings;
};
